import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import type { UseWeekStepperReturn } from "@/hooks/useWeekStepper";
import { parseUserDateInput, findClosestDateOption } from "@/utils/dateUtils";

interface WeekStepperViewProps extends UseWeekStepperReturn {
  className?: string;
  /** Controls the base font-size for scaling (e.g., "0.75rem", "1rem") */
  size?: string;
  inputRef?: React.ForwardedRef<HTMLInputElement>;
  onCommit?: () => void;
  id?: string;
  autoFocus?: boolean;
  testId?: string;
}

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  openUpward: boolean;
}

/**
 * Render-only component for WeekStepper.
 * Receives all state and handlers from useWeekStepper hook.
 */
export function WeekStepperView({
  inputProps,
  stepPrev,
  stepNext,
  listProps,
  containerRef,
  disabled,
  setDropdownOpen,
  className = "",
  size,
  inputRef,
  onCommit,
  id,
  autoFocus = true,
  testId,
}: WeekStepperViewProps) {
  const [inputValue, setInputValue] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [highlightedOffset, setHighlightedOffset] = useState<number | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRowRef = useRef<HTMLDivElement>(null);

  // Get the current selected label from listProps
  const currentLabel = listProps.dates.find(
    d => d.offset === listProps.selectedOffset
  )?.label ?? "";

  // The offset to highlight (keyboard nav takes priority, falls back to selected)
  const activeOffset = highlightedOffset ?? listProps.selectedOffset;

  // Track if user is actively typing (vs using arrow keys)
  const [isTyping, setIsTyping] = useState(false);

  // Guards against onCommit firing more than once per editing session (e.g. when
  // an outside-click and the input's blur both resolve for the same interaction).
  // Reset whenever the input regains focus (handleInputFocus) — i.e. a new session.
  const hasCommittedRef = useRef(false);

  // Single commit + close path. All entry points (blur, outside-click, option
  // click, Enter) funnel through here so the value is persisted exactly once.
  const commitAndClose = useCallback(() => {
    if (hasCommittedRef.current) return;
    hasCommittedRef.current = true;
    setHighlightedOffset(null);
    setIsTyping(false);
    setIsFocused(false);
    textInputRef.current?.blur();
    onCommit?.();
  }, [onCommit]);

  // Shared handler for closing dropdown and committing the selection
  const closeAndCommit = useCallback((offset?: number) => {
    if (offset !== undefined) {
      listProps.onSelect(offset);
    }
    // Delay to allow the React state update from onSelect's onChange to complete
    setTimeout(() => commitAndClose(), 0);
  }, [listProps, commitAndClose]);

  // The hook re-anchors its selectable window when the list opens, so it needs to
  // know. Opening is the deliberate act that earns another year of range.
  useEffect(() => {
    setDropdownOpen(isFocused);
  }, [isFocused, setDropdownOpen]);

  // Calculate dropdown position when focused, and update on scroll/resize
  useLayoutEffect(() => {
    if (!isFocused || !inputRowRef.current) {
      setDropdownPosition(null);
      return;
    }

    const updatePosition = () => {
      if (inputRowRef.current) {
        const rect = inputRowRef.current.getBoundingClientRect();
        const dropdownHeight = 200; // matches max-height in CSS
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUpward = spaceBelow < dropdownHeight && rect.top > spaceBelow;
        setDropdownPosition({
          top: openUpward
            ? rect.top + window.scrollY - dropdownHeight
            : rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          openUpward,
        });
      }
    };

    updatePosition();

    // Update position on scroll/resize
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isFocused]);

  // Sync input value with current selection when not typing
  useEffect(() => {
    if (!isTyping) {
      setInputValue(currentLabel);
      setIsValid(true);
    }
    if (!isFocused) {
      setHighlightedOffset(null);
    }
  }, [currentLabel, isFocused, isTyping]);


  // Auto-focus on mount to immediately show dropdown
  useEffect(() => {
    if (autoFocus && textInputRef.current && !disabled) {
      textInputRef.current.focus();
    }
  }, [autoFocus, disabled]);

  // Commit and close when the user clicks outside the widget (or its portal
  // dropdown). Only active while the widget is focused, so it never fires for a
  // mounted-but-untouched editor, and the commit-once guard keeps it from
  // double-firing with the input's blur path for the same click.
  useEffect(() => {
    if (!isFocused) return;
    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (
        !target ||
        containerRef.current?.contains(target) ||
        listRef.current?.contains(target)
      ) {
        return;
      }
      commitAndClose();
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isFocused, commitAndClose, containerRef]);

  // Scroll to selected option (centered) when list opens, then nearest for navigation
  const hasScrolledOnOpen = useRef(false);
  useEffect(() => {
    if (!isFocused) {
      hasScrolledOnOpen.current = false;
      return;
    }

    const scrollToTarget = () => {
      if (!listRef.current) return;
      const highlightedOption = listRef.current.querySelector('.week-stepper__option--highlighted');
      const selectedOption = listRef.current.querySelector('.week-stepper__option--selected');
      const targetOption = highlightedOption || selectedOption;
      if (targetOption) {
        const block = !hasScrolledOnOpen.current ? 'center' : 'nearest';
        targetOption.scrollIntoView({ block });
        hasScrolledOnOpen.current = true;
      }
    };

    if (!hasScrolledOnOpen.current) {
      // On first open, wait a frame for the portal to mount
      const id = requestAnimationFrame(scrollToTarget);
      return () => cancelAnimationFrame(id);
    }
    scrollToTarget();
  }, [activeOffset, isFocused]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const dates = listProps.dates;
    if (dates.length === 0) return;

    switch (e.key) {
      case "ArrowLeft":
        e.preventDefault();
        setIsTyping(false);
        stepPrev();
        break;
        
      case "ArrowRight":
        e.preventDefault();
        setIsTyping(false);
        stepNext();
        break;
        
      case "ArrowUp": {
        e.preventDefault();
        const currentIdx = dates.findIndex(d => d.offset === activeOffset);
        if (currentIdx > 0) {
          setHighlightedOffset(dates[currentIdx - 1]!.offset);
        }
        break;
      }
      
      case "ArrowDown": {
        e.preventDefault();
        const currentIdx = dates.findIndex(d => d.offset === activeOffset);
        if (currentIdx < dates.length - 1) {
          setHighlightedOffset(dates[currentIdx + 1]!.offset);
        }
        break;
      }
      
      case "Escape":
        e.preventDefault();
        // Cancel - reset to original value and close without committing.
        // Setting the guard blocks the blur-triggered commit that follows.
        hasCommittedRef.current = true;
        setInputValue(currentLabel);
        setIsValid(true);
        setHighlightedOffset(null);
        setIsTyping(false);
        setIsFocused(false);
        textInputRef.current?.blur();
        break;
        
      case "Enter": {
        e.preventDefault();
        // Save and close
        if (highlightedOffset !== null) {
          closeAndCommit(highlightedOffset);
        } else {
          // Try to parse the input value
          const parsed = parseUserDateInput(inputValue);
          if (parsed) {
            const closest = findClosestDateOption(dates, parsed);
            if (closest) {
              setIsValid(true);
              closeAndCommit(closest.offset);
            }
          } else {
            setIsValid(false);
            // Don't close if invalid
          }
        }
        break;
      }
      
      case "Tab":
        // Step the date but keep focus
        e.preventDefault();
        setIsTyping(false);
        if (e.shiftKey) {
          stepPrev();
        } else {
          stepNext();
        }
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setHighlightedOffset(null); // Clear highlight when typing
    setIsTyping(true); // User is actively typing
    
    // Validate on each keystroke
    if (value.trim()) {
      const parsed = parseUserDateInput(value);
      setIsValid(parsed !== null);
    } else {
      setIsValid(true);
    }
  };

  const handleInputFocus = () => {
    // New editing session: re-arm the commit-once guard.
    hasCommittedRef.current = false;
    setInputValue(currentLabel);
    setIsTyping(false);
    setIsFocused(true);
    
    // Find the closest option to highlight (by offset distance from selected)
    const dates = listProps.dates;
    if (dates.length > 0) {
      const selectedDate = dates.find(d => d.offset === listProps.selectedOffset);
      if (selectedDate) {
        setHighlightedOffset(listProps.selectedOffset);
      } else {
        // Selected date not in list, find closest by offset
        const closest = dates.reduce((prev, curr) =>
          Math.abs(curr.offset - listProps.selectedOffset) < Math.abs(prev.offset - listProps.selectedOffset)
            ? curr : prev
        );
        setHighlightedOffset(closest.offset);
      }
    } else {
      setHighlightedOffset(null);
    }
    
    textInputRef.current?.select();
  };

  const handleInputBlur = () => {
    // Delay to allow click on list items to complete before closing
    setTimeout(() => {
      // If focus moved to the list container, don't close yet
      if (listRef.current?.contains(document.activeElement)) {
        return;
      }
      // Save and close (commit-once guard dedupes against the outside-click path)
      commitAndClose();
    }, 150);
  };

  const handleOptionClick = (offset: number) => {
    closeAndCommit(offset);
  };

  return (
    <div
      ref={containerRef}
      className={`week-stepper ${disabled ? "week-stepper--disabled" : ""} ${isFocused ? "week-stepper--focused" : ""} ${className}`}
      style={size ? { fontSize: size } : undefined}
    >
      {/* Hidden input for form submission */}
      <input
        ref={inputRef}
        type="hidden"
        value={inputProps["data-value"]}
        id={id}
      />

      {/* Top row: prev button, input, next button */}
      <div ref={inputRowRef} className="week-stepper__input-row">
        <button
          type="button"
          className="week-stepper__nav week-stepper__nav--prev"
          onClick={stepPrev}
          onMouseDown={(e) => e.preventDefault()}
          disabled={disabled}
          tabIndex={-1}
          aria-label="Previous week (-7 days)"
          title="-7"
        >
          ‹
        </button>

        <input
          ref={textInputRef}
          type="text"
          className={`week-stepper__input ${!isValid ? "week-stepper__input--invalid" : ""}`}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          disabled={disabled}
          aria-label="Week date selector"
          placeholder="Mon DD"
          autoComplete="off"
          data-testid={testId}
        />

        <button
          type="button"
          className="week-stepper__nav week-stepper__nav--next"
          onClick={stepNext}
          onMouseDown={(e) => e.preventDefault()}
          disabled={disabled}
          tabIndex={-1}
          aria-label="Next week (+7 days)"
          title="+7"
        >
          ›
        </button>
      </div>

      {/* Date list - rendered via portal to document.body for proper z-index stacking */}
      {isFocused && dropdownPosition && createPortal(
        <div 
          ref={listRef} 
          className={`week-stepper__list-container week-stepper__list-container--portal ${dropdownPosition.openUpward ? "week-stepper__list-container--upward" : ""}`}
          style={{
            position: 'absolute',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            fontSize: size,
          }}
        >
          <div className="week-stepper__list">
            {listProps.dates.map((option) => {
              const isSelected = option.offset === listProps.selectedOffset;
              const isHighlighted = option.offset === highlightedOffset;
              const weekDelta = option.offset - listProps.selectedOffset;
              const deltaLabel = weekDelta === 0 ? "0" : weekDelta > 0 ? `+${weekDelta}` : `${weekDelta}`;
              return (
                <button
                  key={option.offset}
                  type="button"
                  className={`week-stepper__option ${isSelected ? "week-stepper__option--selected" : ""} ${isHighlighted ? "week-stepper__option--highlighted" : ""}`}
                  onClick={() => handleOptionClick(option.offset)}
                  disabled={disabled}
                  data-testid="week-date-option"
                  data-value={option.isoValue}
                >
                  <span className="week-stepper__delta">{deltaLabel}</span>{option.label}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
